import { apiGet, apiSend } from '../../session/client';
import { type Order, listOrders } from '../orders/orders-api';

/**
 * Everything the Bookings page reads and writes, and nothing it invents.
 *
 * Each call is an existing route — the ones the Android app's appointment
 * screens (section 13) and consultation screens (25.07) already use:
 *
 * - `me/bookings` — the signed-in person's appointments (list, one, cancel).
 *   It carries ids only, so the service and the shop are read from the
 *   listing (`consumer/offers/:id`), in parallel, as Android does.
 * - `consumer/consultations` — their online consultations (list, one, reply).
 * - `consumer/orders` — the orders a consultation was paid on, for "Paid".
 *
 * There is no person id in any path or body: the server decides whose these are.
 */

export type BookingStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
export type PaymentState = 'NOT_REQUIRED' | 'REQUIRED' | 'PAID';

export interface Appointment {
  readonly id: string;
  readonly listingId: string;
  readonly catalogueItemId: string;
  readonly storefrontId: string;
  readonly branchId?: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timeZone: string;
  readonly mode: 'IN_PERSON' | 'VIRTUAL' | string;
  readonly status: BookingStatus;
  readonly paymentState: PaymentState;
  readonly createdAt: string;
}

interface AppointmentPage {
  readonly items: readonly Appointment[];
  readonly pageInfo: { readonly nextCursor?: string; readonly hasMore: boolean };
}

export type ConsultationStatus = 'SUBMITTED' | 'IN_ORDER' | 'MORE_INFO' | 'APPROVED' | 'DECLINED' | 'EXPIRED';

export interface ConsultationMessage {
  readonly from: 'PRESCRIBER' | 'CUSTOMER';
  readonly text: string;
  readonly at: string;
  readonly request?: {
    readonly kind: 'TEXT' | 'DOCUMENT' | 'MEASUREMENT';
    readonly unit?: string;
    readonly dueAt?: string;
  };
  readonly documentIds?: readonly string[];
  readonly measurement?: { readonly value: number; readonly unit: string };
}

export interface ConsultationAnswer {
  readonly questionId: string;
  readonly prompt: string;
  readonly kind?: string;
  readonly value: unknown;
  readonly labels?: readonly string[];
}

export interface Consultation {
  readonly id: string;
  readonly reference: string;
  readonly status: ConsultationStatus;
  readonly listingId: string;
  readonly storefrontId: string;
  readonly itemName: string;
  readonly title?: string;
  readonly submittedAt: string;
  readonly expiresAt: string;
  readonly orderId?: string;
  readonly messages: readonly ConsultationMessage[];
  readonly decision?: {
    readonly outcome: 'APPROVED' | 'DECLINED';
    readonly at: string;
    readonly reason?: string;
  };
  readonly answers: readonly ConsultationAnswer[];
}

/** What a booking's listing says about the service and the shop. */
export interface Listing {
  readonly id: string;
  readonly title: string;
  readonly storefront: { readonly id: string; readonly slug: string; readonly name: string };
  readonly locations: readonly {
    readonly id: string;
    readonly town?: string;
    readonly postcode?: string;
  }[];
  readonly price: { readonly resolved: boolean; readonly currency?: string; readonly amountMinor?: number };
}

const id = (value: string): string => encodeURIComponent(value);

/** Pages read before the list says "at least": 5 × 100 appointments. */
export const APPOINTMENT_PAGES = 5;

/**
 * Every appointment, page by page up to a ceiling — reading only the first
 * page would quietly drop the rest (the capped-list trap). Past the ceiling
 * the page says the list is not complete.
 */
export async function listAppointments(): Promise<{ items: readonly Appointment[]; complete: boolean }> {
  const items: Appointment[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < APPOINTMENT_PAGES; page += 1) {
    const found = await apiGet<AppointmentPage>(
      `/me/bookings?limit=100${cursor !== undefined ? `&cursor=${id(cursor)}` : ''}`,
    );
    items.push(...found.items);
    if (!found.pageInfo.hasMore || found.pageInfo.nextCursor === undefined) return { items, complete: true };
    cursor = found.pageInfo.nextCursor;
  }
  return { items, complete: false };
}

export const getAppointment = (bookingId: string): Promise<Appointment> =>
  apiGet<Appointment>(`/me/bookings/${id(bookingId)}`);

/** The platform's own rule decides: PENDING or CONFIRMED may be cancelled, nothing else. */
export const cancelAppointment = (bookingId: string): Promise<Appointment> =>
  apiSend<Appointment>('POST', `/me/bookings/${id(bookingId)}/cancel`);

export const listConsultations = (): Promise<readonly Consultation[]> =>
  apiGet<{ consultations: Consultation[] }>('/consumer/consultations').then((found) => found.consultations);

export const getConsultation = (consultationId: string): Promise<Consultation> =>
  apiGet<Consultation>(`/consumer/consultations/${id(consultationId)}`);

/** 25.07.13: the customer's answer to the prescriber's question. */
export const replyToPrescriber = (
  consultationId: string,
  reply: {
    readonly text: string;
    readonly documentIds?: readonly string[];
    readonly measurement?: { readonly value: number };
  },
): Promise<Consultation> =>
  apiSend<Consultation>('POST', `/consumer/consultations/${id(consultationId)}/reply`, reply);

/** A photo or document for a reply, kept in the patient's own Documents. */
export const uploadEvidence = (filename: string, dataBase64: string): Promise<{ id: string }> =>
  apiSend<{ id: string }>('POST', '/consumer/consultations/evidence', { filename, dataBase64 });

export const getListing = (listingId: string): Promise<Listing> =>
  apiGet<Listing>(`/consumer/offers/${id(listingId)}`);

/**
 * The listings behind a set of bookings, each read once and in parallel. One
 * that cannot be read (withdrawn since) is simply absent; the page falls back
 * to what the booking itself recorded.
 */
export async function getListings(listingIds: readonly string[]): Promise<ReadonlyMap<string, Listing>> {
  const unique = [...new Set(listingIds)];
  const found = await Promise.all(unique.map((listingId) => getListing(listingId).catch(() => undefined)));
  return new Map(
    found
      .filter((listing): listing is Listing => listing !== undefined)
      .map((listing) => [listing.id, listing]),
  );
}

/** The person's orders, by id — the ones consultations were paid on. Best effort. */
export async function getOrdersById(): Promise<ReadonlyMap<string, Order>> {
  const orders = await listOrders().catch(() => [] as readonly Order[]);
  return new Map(orders.map((order) => [order.id, order]));
}
